import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {announce} from '@github-ui/aria-live'
import {Banner} from '@primer/react'
import {useCallback, useEffect, useId, useRef, useState} from 'react'
import type {HeaderPageData} from '../page-data/payloads/header'
import {PullRequestHiddenCharactersBanner} from './banners/PullRequestHiddenCharactersBanner'
import {PullRequestAutomatedSecurityUpdateBanner} from './banners/PullRequestAutomatedSecurityUpdateBanner'
import {PullRequestPausedDependabotBanner} from './banners/PullRequestPausedDependabotBanner'
import {PullRequestState} from '../types'
import {
  PROOF_OF_PRESENCE_AUTHENTICATION_FAILED_EVENT,
  PROOF_OF_PRESENCE_AUTHENTICATION_SUCCEEDED_EVENT,
  type ProofOfPresenceAuthenticationEventDetail,
} from '../helpers/constants'

const PROOF_OF_PRESENCE_FAILURE_TITLE = 'Proof of presence was not satisfied'

function proofOfPresenceFailureDescription(requiredLevel: ProofOfPresenceAuthenticationEventDetail['requiredLevel']) {
  if (requiredLevel === 'mfa') {
    return 'Your authentication did not satisfy this requirement. Multi-factor authentication (MFA) is required.'
  }
  if (requiredLevel === 'reauth') {
    return 'Your authentication did not satisfy this requirement. Re-authentication is required.'
  }
  return 'Your authentication did not satisfy the current proof of presence requirement.'
}

function restoreAuthenticationFocus(failure: ProofOfPresenceAuthenticationEventDetail) {
  const currentControls = Array.from(
    document.querySelectorAll<HTMLButtonElement>('[data-proof-of-presence-authenticate]'),
  ).filter(button => button.getAttribute('data-proof-of-presence-authenticate') === failure.pullRequestPath)
  for (const button of [failure.returnFocusTarget, ...currentControls]) {
    if (!button?.isConnected || button.disabled || button.getAttribute('aria-disabled') === 'true') continue
    button.focus()
    if (document.activeElement === button) return
  }
}

export function PullRequestBanners({
  bannersData,
  pullRequest,
  repository,
}: Pick<HeaderPageData, 'bannersData' | 'pullRequest' | 'repository'>) {
  const [proofOfPresenceFailures, setProofOfPresenceFailures] = useState(
    () => new Map<string, ProofOfPresenceAuthenticationEventDetail>(),
  )
  const pendingFailureFocusRef = useRef<ProofOfPresenceAuthenticationEventDetail | null>(null)
  const failureDescriptionId = useId()

  const clearProofOfPresenceFailure = useCallback((pullRequestPath: string) => {
    setProofOfPresenceFailures(previous => {
      if (!previous.has(pullRequestPath)) return previous
      const failures = new Map(previous)
      failures.delete(pullRequestPath)
      return failures
    })
  }, [])

  useEffect(() => {
    const showProofOfPresenceAuthenticationFailure = (event: Event) => {
      const detail = (event as CustomEvent<ProofOfPresenceAuthenticationEventDetail>).detail
      if (detail?.pullRequestPath !== pullRequest.pathName) return
      pendingFailureFocusRef.current = detail
      setProofOfPresenceFailures(previous => new Map(previous).set(detail.pullRequestPath, detail))
    }
    const clearProofOfPresenceAuthenticationFailure = (event: Event) => {
      const detail = (event as CustomEvent<ProofOfPresenceAuthenticationEventDetail>).detail
      if (detail?.pullRequestPath) clearProofOfPresenceFailure(detail.pullRequestPath)
    }
    window.addEventListener(PROOF_OF_PRESENCE_AUTHENTICATION_FAILED_EVENT, showProofOfPresenceAuthenticationFailure)
    window.addEventListener(PROOF_OF_PRESENCE_AUTHENTICATION_SUCCEEDED_EVENT, clearProofOfPresenceAuthenticationFailure)
    return () => {
      window.removeEventListener(
        PROOF_OF_PRESENCE_AUTHENTICATION_FAILED_EVENT,
        showProofOfPresenceAuthenticationFailure,
      )
      window.removeEventListener(
        PROOF_OF_PRESENCE_AUTHENTICATION_SUCCEEDED_EVENT,
        clearProofOfPresenceAuthenticationFailure,
      )
    }
  }, [clearProofOfPresenceFailure, pullRequest.pathName])

  const currentProofOfPresenceFailure = proofOfPresenceFailures.get(pullRequest.pathName)
  const currentProofOfPresenceFailureDescription = currentProofOfPresenceFailure
    ? proofOfPresenceFailureDescription(currentProofOfPresenceFailure.requiredLevel)
    : null

  const focusFailureBanner = useCallback(
    (banner: HTMLDivElement | null) => {
      if (
        !banner ||
        !currentProofOfPresenceFailure ||
        pendingFailureFocusRef.current !== currentProofOfPresenceFailure
      ) {
        return
      }
      pendingFailureFocusRef.current = null
      const wasFocused = document.activeElement === banner
      banner.focus()
      // Focusing the named, described region announces this essential error. Use aria-live
      // only when focus cannot move or a new failure updates the already-focused banner.
      if (wasFocused || document.activeElement !== banner) {
        announce(`${PROOF_OF_PRESENCE_FAILURE_TITLE}. ${currentProofOfPresenceFailureDescription}`, {assertive: true})
      }
    },
    [currentProofOfPresenceFailure, currentProofOfPresenceFailureDescription],
  )

  return (
    <ErrorBoundary boundaryName="PullRequestBanners" fallback={null}>
      {currentProofOfPresenceFailure && currentProofOfPresenceFailureDescription && (
        <Banner
          ref={focusFailureBanner}
          className="width-full"
          variant="critical"
          title={PROOF_OF_PRESENCE_FAILURE_TITLE}
          aria-describedby={failureDescriptionId}
          onDismiss={() => {
            restoreAuthenticationFocus(currentProofOfPresenceFailure)
            clearProofOfPresenceFailure(currentProofOfPresenceFailure.pullRequestPath)
          }}
        >
          <Banner.Description id={failureDescriptionId}>{currentProofOfPresenceFailureDescription}</Banner.Description>
        </Banner>
      )}
      {bannersData.banners.hiddenCharacterWarning.render && (
        <PullRequestHiddenCharactersBanner pullRequest={pullRequest} />
      )}
      {bannersData.banners.pausedDependabotUpdate.render && (
        <PullRequestPausedDependabotBanner repository={repository} />
      )}
      {bannersData.banners.dependabotAutomatedSecurityUpdates.render && pullRequest.state === PullRequestState.Open && (
        <PullRequestAutomatedSecurityUpdateBanner
          dependabotUpdates={bannersData.banners.dependabotAutomatedSecurityUpdates}
          pullRequest={pullRequest}
        />
      )}
    </ErrorBoundary>
  )
}
